import React, { useEffect, useState } from "react";
import axios from "axios";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { Typography, useTheme } from "@mui/material";
import { getCurrentTimestamp } from "../../../utils/dateUtils";
import ClickableImage from "./Images/ClickableImage";
import ClickableScrollImage from "./Images/ClickableScrollImage";
import ImageScrollPreview from "./Images/ImageScrollPreview";
import ScrollImageModal from "../../common/userFeedback/modals/ScrollImageModal";
import NoDataScreenCover from "../../common/userFeedback/NoDataScreenCover";

function SingleSelectGarmentWindow({
  selectedGarment,
  setSelectedGarment,
  disabled = false,
  additionalComponents = [],
  garments,
  imageIndex = 1,
  numberOfColumns = 6,
  scrollPreview = false,
  multiImageClickablePreview=false,
  spacing = 2,
  showNoDataScreen = false,
  showId = true,
  groupRepresentativeBorder=false
}) {
  const { palette, typography } = useTheme();

  const [open, setOpen] = useState(false);
  const [imgXs, setImgXs] = useState(6)
  const [imgHeight, setImgHeight] = useState("10rem")
  const [selectedGarmentImages, setSelectedGarmentImages] = useState(null);

  const handleOpen = (garmentImages) => {
    setSelectedGarmentImages(garmentImages);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedGarmentImages(null);
    setOpen(false);
  };

  useEffect(() => {
    setImgXs(numberOfColumns <= 6 ? 6 : 12)
    setImgHeight(numberOfColumns <= 4 ? "12rem" : (numberOfColumns <= 6 ? "10rem" : "6rem"))
  }, [numberOfColumns])

  return (
    <Box
      sx={{
        backgroundColor: palette.primary[100],
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        mb: 1,
        br: 2,
      }}
    >
      <Grid
        container
        sx={{
          maxHeight: "90%",
        }}
      >
        <ScrollImageModal
          handleClose={handleClose}
          openModal={open}
          images={selectedGarmentImages}
        />
        {garments.map((garment, index) => {
          const isSelected =
            selectedGarment.garment_id === garment.garment_id;
          const garmentImages = Array.from(
            { length: garment.num_images },
            (_, i) => i
          ).map(
            (num) =>
              `https://scraped-garment-images.s3.eu-west-2.amazonaws.com/${
                garment.uuid_public_id
              }-${num + 1}.jpg`
          );
          return (
            <Grid
              item
              xs={12 / numberOfColumns - 0.25}
              key={garment.garment_id}
              onClick={(e) => {
                setSelectedGarment({ ...garment, index: index + 1 });
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                handleOpen(garmentImages);
              }}
              sx={{
                mr: 1,
                mb: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                backgroundColor: palette.primary[200],
                borderRadius: 2,
                p: scrollPreview ? 0 : 2,
                pt: scrollPreview ? 0 : 2,
                border: isSelected
                  ? ((garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? `4px solid ${palette.success[400]}` : `4px dashed ${palette.success[400]}`)
                  : ((garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? `1px solid ${palette.primary[600]}` : (garment.is_published ? `4px dashed ${palette.warning[500]}` : `4px dashed ${palette.primary[700]}`)),
                maxHeight: "calc(100vh / 4)",
                cursor: "pointer",
              }}
            >
              {!scrollPreview ? (
                multiImageClickablePreview ? (
                  <ClickableScrollImage numImages={garmentImages?.length} garmentUUID={garment?.uuid_public_id} image={`https://scraped-garment-images.s3.eu-west-2.amazonaws.com/${garment.uuid_public_id}-1.jpg`} sxImg={{ height: 96, width: 96, paddingY: 2, float: "left" }} overlayColour={(garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? null : (garment.is_published ? `rgba(255, 178, 51, 0.2)` : `rgba(0,0,0,0.2)`)}/>
                ) : (
                  <ClickableImage image={imageIndex > garmentImages.length ? null : garmentImages[imageIndex - 1]} overlayColour={(garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? null : (garment.is_published ? `rgba(255, 178, 51, 0.2)` : `rgba(0,0,0,0.2)`)}/>
                )
              ) : (
                <ImageScrollPreview
                  imgXs={imgXs}
                  imgHeight={imgHeight}
                  images={garmentImages}
                  spacing={0.5}
                  overlayColour={(garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? null : (garment.is_published ? `rgba(255, 178, 51, 0.2)` : `rgba(0,0,0,0.2)`)}
                  groupRepresentativeBorder={groupRepresentativeBorder}
                />
              )}

              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: 20,
                  zIndex: 11,
                  pl: 0.5,
                  pr: 1,
                  pb: 2,
                  borderTopLeftRadius: 8,
                  borderBottomRightRadius: 4,
                  backgroundColor: (garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? palette.primary[100] : (garment.is_published ? "rgba(184, 134, 11, 0.75)" : "rgba(0,0,0,0.75)"),
                  color: (garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? palette.primary[800] : palette.primary[100],
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap", 
                  overflow: "hidden",
                  maxWidth: "100%",
                }}
              >
                <Typography variant="body_sm" sx={{ fontSize: numberOfColumns <= 4 ? '1rem' : '0.65rem', textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                  {garment.score}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  bottom: 18,
                  left: 0,
                  height: 20,
                  zIndex: 11,
                  pr: 1,
                  pl: 1,
                  borderRadius: 2,
                  backgroundColor: (garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? palette.primary[100] : (garment.is_published ? "rgba(184, 134, 11, 0.75)" : "rgba(0,0,0,0.75)"),
                  color: (garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? palette.primary[800] : palette.primary[100],
                  userSelect: "text",
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap", 
                  overflow: "hidden",
                  maxWidth: "100%",
                }}
              >
                <Typography variant="body_sm" sx={{ fontSize: numberOfColumns <= 4 ? '1rem' : '0.65rem', textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                  <b>REP ID:</b> {garment.garment_id}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  display: "flex",
                  height: 20,
                  zIndex: 11,
                  pl: 1,
                  pr: 2,
                  borderRadius: 2,
                  backgroundColor: (garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? palette.primary[100] : (garment.is_published ? "rgba(184, 134, 11, 0.75)" : "rgba(0,0,0,0.75)"),
                  color: (garment.can_change_rep === null || garment.can_change_rep === undefined) || garment.can_change_rep ? palette.primary[800] : palette.primary[100],
                  userSelect: "text", 
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap", 
                  overflow: "hidden",
                  maxWidth: "100%",
                }}
              >
                <Typography variant="body_sm" sx={{ fontSize: numberOfColumns <= 4 ? '1rem' : '0.65rem', textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                  <b>GROUP ID:</b> {garment.group_id}
                </Typography>
              </Box>
            </Grid>
          );
        })}
        {additionalComponents.map((component) => {
          return component;
        })}
        {garments.length === 0 && showNoDataScreen && (
          <NoDataScreenCover showBackButton={false} />
        )}
      </Grid>
    </Box>
  );
}
export default SingleSelectGarmentWindow;
